'use client';

import { createContext, useCallback, useContext, useMemo, useState } from "react";

const INITIAL_MOUSE = {
    status: "pending",
    messageShown: false,
    tests: [
        { title: "Click 'n Drag Test", name: "LeftButtonTest", result: "initial" },
        { title: "Mouse Accuracy Test", name: "PrecisionTest", result: "initial" },
        { title: "Scroll Test", name: "ScrollTest", result: "initial" },
    ],
};

const MouseStateContext = createContext(null);

function deriveStatus(tests) {
    const results = tests.map((t) => t?.result);
    const anyFail = results.some((r) => r === "failed");
    const allPass = results.length > 0 && results.every((r) => r === "success" || r === "passed");
    const anyInitial = results.some((r) => r === "initial");
    if (anyFail) return "failed";
    if (allPass) return "passed";
    if (anyInitial) return "pending";
    return "running";
}

export function MouseStateProvider({ children }) {
    const [mouse, setMouse] = useState(INITIAL_MOUSE);
    const [resultModal, setResultModal] = useState(null);

    const updateMouse = useCallback((payload) => {
        if (!payload?.mouse) return;
        setMouse((prev) => {
            const next = { ...prev, ...payload.mouse };
            if (Array.isArray(next.tests)) {
                next.status = deriveStatus(next.tests);
            }
            return next;
        });
    }, []);

    const showModal = useCallback((data) => setResultModal(data), []);
    const closeModal = useCallback(() => setResultModal(null), []);

    const resetAll = useCallback(() => {
        setMouse({ ...INITIAL_MOUSE, tests: INITIAL_MOUSE.tests.map((t) => ({ ...t })) });
        setResultModal(null);
    }, []);

    const value = useMemo(
        () => ({ mouse, updateMouse, resultModal, showModal, closeModal, resetAll }),
        [mouse, updateMouse, resultModal, showModal, closeModal, resetAll]
    );

    return <MouseStateContext.Provider value={value}>{children}</MouseStateContext.Provider>;
}

export function useMouseState() {
    const ctx = useContext(MouseStateContext);
    if (!ctx) throw new Error("useMouseState must be used within MouseStateProvider");
    return ctx;
}
